// Authenticated fetch for user-initiated API calls (L3A2-5).
//
// On 2026-10-13 a launch from the UI did nothing: the page's Entra ID token
// had expired, `/api/projects/start` answered 401 three times, and the error
// went only to the console. App acquires its ID token once, when the page
// loads, and MSAL's silent cache lookup checks the ACCESS token's expiry, not
// the ID token's (msal-common 14 `SilentFlowClient.acquireCachedToken`), so
// a page left open past the ID token's lifetime keeps sending a dead token.
//
// The server's answer to an expired token (auth.rs `auth_middleware`) is
// status 401 with `{"success": false, "error": "Token validation failed"}`.
// The cause (`ExpiredSignature`) is in the server log only, and a token that
// is valid but outside the security group gets the same 401, which is why a
// sign-in is attempted at most once in SIGN_IN_RETRY_MS: a second redirect
// would loop for a user sign-in cannot help.

import type { IPublicClientApplication } from '@azure/msal-browser'

export interface AuthSession {
  /** Headers carrying the current token. */
  getHeaders(): Record<string, string>
  /** Silently re-acquire a token. True when a new one is in place for getHeaders. */
  refresh(): Promise<boolean>
  /** Start an interactive sign-in (a redirect). Absent when there is none (API-key or dev mode). */
  signIn?: () => Promise<void>
}

/** The request needs a sign-in; the message says whether one has started. */
export class SignInRequiredError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SignInRequiredError'
  }
}

const SIGN_IN_MARK = 'starforge_signin_redirect_at'
export const SIGN_IN_RETRY_MS = 5 * 60 * 1000

function readMark(): number | null {
  try {
    const v = sessionStorage.getItem(SIGN_IN_MARK)
    return v ? Number(v) : null
  } catch {
    return null
  }
}

function writeMark(value: number | null) {
  try {
    if (value === null) sessionStorage.removeItem(SIGN_IN_MARK)
    else sessionStorage.setItem(SIGN_IN_MARK, String(value))
  } catch {
    // Storage blocked: the guard then only holds for this page.
  }
}

/** The server's error text for a failed response: its JSON `error`, else the body, else the status. */
export async function responseError(response: Response): Promise<string> {
  let text = ''
  try {
    text = await response.text()
  } catch {
    // fall through to the status
  }
  if (text) {
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed.error === 'string' && parsed.error) return parsed.error
    } catch {
      // not JSON: use the body as it is
    }
    return text.slice(0, 300)
  }
  return `HTTP ${response.status}`
}

/**
 * fetch with the session's token. A 401 gets one silent refresh and one retry;
 * if that cannot fix it, the user is sent to sign in once, and SignInRequiredError
 * is thrown either way so the caller shows why nothing happened. Any other
 * response is returned to the caller as is.
 */
export async function authFetch(url: string, init: RequestInit, session: AuthSession): Promise<Response> {
  const send = () => fetch(url, { ...init, headers: { ...session.getHeaders(), ...(init.headers as Record<string, string> | undefined) } })

  let response = await send()
  if (response.status === 401) {
    let refreshed = false
    try {
      refreshed = await session.refresh()
    } catch {
      refreshed = false
    }
    if (refreshed) response = await send()
  }
  if (response.status !== 401) {
    if (response.ok) writeMark(null)
    return response
  }

  const serverError = await responseError(response)
  const mark = readMark()
  const recentlyRedirected = mark !== null && Date.now() - mark < SIGN_IN_RETRY_MS
  if (session.signIn && !recentlyRedirected) {
    // Marked before the redirect starts: a redirect navigates away, so code
    // after a successful signIn() may never run.
    writeMark(Date.now())
    try {
      await session.signIn()
    } catch (err) {
      // No redirect happened (e.g. MSAL's interaction_in_progress): clear
      // the mark, or the next attempt would blame the security group.
      writeMark(null)
      const msg = err instanceof Error ? err.message : String(err)
      throw new SignInRequiredError(`Your sign-in has expired, and signing in again could not start: ${msg}`)
    }
    throw new SignInRequiredError(`Your sign-in has expired. Redirecting you to sign in again (server said: ${serverError}).`)
  }
  if (session.signIn) {
    throw new SignInRequiredError(
      `The server still refuses your sign-in after signing in again (server said: ${serverError}). ` +
        'Check that your account is in the Star Forge security group, or sign out and back in.',
    )
  }
  throw new SignInRequiredError(`Not authorised (server said: ${serverError}).`)
}

/**
 * POST JSON and read a `{success, error}` reply. Resolves with the reply on
 * success; throws an Error carrying the server's message otherwise.
 */
export async function postAction<T extends { success?: boolean; error?: string }>(
  url: string,
  body: unknown,
  session: AuthSession,
): Promise<T> {
  const response = await authFetch(url, { method: 'POST', body: JSON.stringify(body) }, session)
  if (!response.ok) throw new Error(await responseError(response))
  let data: T
  try {
    data = (await response.json()) as T
  } catch {
    throw new Error(`Unreadable reply from ${url} (HTTP ${response.status})`)
  }
  if (!data.success) throw new Error(data.error || `${url} failed without a message`)
  return data
}

export type MsalLike = Pick<IPublicClientApplication, 'getAllAccounts' | 'acquireTokenSilent' | 'acquireTokenRedirect'>

/**
 * The session App uses: tokens from MSAL. `tokenRef` is what getHeaders reads.
 * refresh() writes the new token into it before returning, because the retry
 * is sent straight after the await; in a browser a setState made there has
 * not rendered yet, so `onToken` alone would put the old token on the retry.
 */
export function createMsalSession(
  msal: MsalLike | null,
  tokenRef: { current: string | null },
  onToken: (token: string) => void,
  scopes: string[],
): AuthSession {
  return {
    getHeaders: () => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (tokenRef.current) headers['Authorization'] = `Bearer ${tokenRef.current}`
      return headers
    },
    refresh: async () => {
      const account = msal?.getAllAccounts()[0]
      if (!msal || !account) return false
      try {
        // forceRefresh: MSAL's cache lookup checks the access token's expiry,
        // not the ID token's, so without it a dead ID token comes back.
        const result = await msal.acquireTokenSilent({ account, scopes, forceRefresh: true })
        if (!result.idToken) return false
        tokenRef.current = result.idToken
        onToken(result.idToken)
        return true
      } catch (err) {
        console.warn('Silent token refresh failed:', err)
        return false
      }
    },
    signIn: msal
      ? async () => { await msal.acquireTokenRedirect({ scopes }) }
      : undefined,
  }
}

/** True when an ID token's `exp` claim (seconds) has passed. No claim, no verdict: false. */
export function idTokenExpired(claims: unknown, nowMs = Date.now()): boolean {
  const exp = (claims as { exp?: unknown } | null | undefined)?.exp
  return typeof exp === 'number' && exp * 1000 <= nowMs
}
